import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'src/app/core/services/user.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { Question, QuestionType } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';
import { KycMismatchAction, LogFixAttemptDTO, FixAttemptType } from 'src/app/core/models/kyc-mismatch.model';
import { TranslateService } from '@ngx-translate/core';

/**
 * Modal modes based on the mismatch action type
 *
 * ACCEPT_CHANGES: Show diff, button to accept the document name
 * FIX_CHANGES: Blind input field to type name, verify button
 * VERIFY_CHANGES: Show options A/B/C without identifying which is which
 * REUPLOAD: Just reupload button
 * LEGACY: Legacy mode for backward compatibility (old 3 scenarios)
 */
export type ModalMode = 'ACCEPT_CHANGES' | 'FIX_CHANGES' | 'VERIFY_CHANGES' | 'REUPLOAD' | 'LEGACY';

/**
 * Option selected in VERIFY_CHANGES mode
 */
export type VerifyChoice = 'option_a' | 'option_b' | 'option_c' | null;

export interface LegalNameFixDialogData {
  userId: string;
  currentLegalName: string;
  veriffName: string;
  kycCheckId: string;
  doneActionId?: string;
  issues?: string[];
  documentData?: {
    legalName?: string;
    dateOfBirth?: string;
    idNumber?: string;
  };
  // New fields for Names Mismatch classification
  mismatchAction?: KycMismatchAction;
  mismatchActions?: KycMismatchAction[]; // Multiple actions allowed
  /**
   * Lógica de ações múltiplas:
   * - OR: usuário ESCOLHE uma das opções (B1: ACCEPT ou REUPLOAD)
   * - AND: usuário DEVE fazer todas em sequência (C: FIX depois REUPLOAD)
   * - TRY_FIX: tenta corrigir primeiro, se revalidação falhar vai para REUPLOAD
   */
  actionLogic?: 'OR' | 'AND' | 'TRY_FIX';
  category?: string;
  subCategory?: string;
  suggestedCorrection?: string;
}

export interface LegalNameFixDialogResult {
  updated: boolean;
  resubmitDocuments?: boolean;
  revalidated?: boolean;
  passed?: boolean | null;
  newLegalName?: string;
  newDateOfBirth?: string;
  newCpf?: string;
  kycCheckId?: string;
  message?: string;
  action?: 'accepted' | 'fixed' | 'reupload' | 'cancelled';
}

@Component({
  selector: 'app-legal-name-fix-modal',
  templateUrl: './legal-name-fix-modal.component.html',
  styleUrls: ['./legal-name-fix-modal.component.scss'],
})
export class LegalNameFixModalComponent implements OnInit {
  loading = true;
  saving = false;
  error = '';
  nameError = '';
  dateOfBirthError = '';
  cpfError = '';

  // Legacy step control (for backward compatibility)
  currentStep: 'confirm' | 'edit' | 'correct-document' = 'confirm';
  isNameMismatchOnly = false;

  // NEW: Modal mode based on action type
  modalMode: ModalMode = 'LEGACY';
  availableActions: KycMismatchAction[] = [];

  // NEW: Step control for new modes
  mismatchStep: 'main' | 'fix_input' | 'reupload_confirm' = 'main';

  // Flag to track if we came from VERIFY_CHANGES option C
  fromVerifyOptionC = false;

  currentName = '';
  veriffName = '';
  newLegalName = '';

  // NEW: For FIX_CHANGES mode - blind input
  fixedName = '';
  nameVerified = false;
  verifying = false;
  verifyError = '';

  // NEW: For VERIFY_CHANGES mode - A/B/C options
  verifyChoice: VerifyChoice = null;
  optionA = '';
  optionB = '';
  optionAIsLegal = false; // Internal control of which is which

  currentDateOfBirth = '';
  newDateOfBirth = '';
  newDateOfBirthDate: Date | null = null;

  currentCpf = '';
  newCpf = '';
  cpfLeaked = false;

  cpfQuestion: Question = {
    id: 'cpf-leak-check',
    created_at: new Date(),
    name: 'CPF Leak Check',
    slug: 'cpf-leak-check',
    statement: '',
    response_type: QuestionType.CPF_LEAK_CHECK,
    configuration: null,
    questionnaire_id: '',
    elective: false,
    answer: undefined,
    isValid: false
  };

  constructor(
    public dialogRef: MatDialogRef<LegalNameFixModalComponent, LegalNameFixDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: LegalNameFixDialogData,
    private userService: UserService,
    private kycService: KycService,
    private http: HttpClient,
    private translate: TranslateService
  ) {
    this.veriffName = data.veriffName;

    const hasValidDocumentData = this.veriffName && this.veriffName.trim() !== '';

    // Mismatch flow — the only flow this modal serves now. The plain "amend your
    // details" flow was moved to PersonalDetailsModalComponent.
    {
      // Determine modal mode based on actions
      this.availableActions = data.mismatchActions || (data.mismatchAction ? [data.mismatchAction] : []);

      // 🔧 FIX: Se não há dados do documento (veriffName vazio/Not set),
      // forçar FIX_CHANGES + REUPLOAD com lógica AND
      if (!hasValidDocumentData && this.availableActions.length === 0) {
        console.log('[Legal Name Fix] No document data - forcing FIX_CHANGES + REUPLOAD (AND)');
        this.availableActions = ['FIX_CHANGES', 'REUPLOAD'];
        this.data.actionLogic = 'AND';
        this.data.mismatchActions = ['FIX_CHANGES', 'REUPLOAD'];
      }
      // 🔧 FIX: Se não há mismatchActions mas HÁ veriffName, usar FIX_CHANGES como default
      // (ao invés de LEGACY que mostra botão de "amend" problemático)
      else if (this.availableActions.length === 0 && hasValidDocumentData) {
        console.log('[Legal Name Fix] No mismatch actions but has document data - defaulting to FIX_CHANGES + REUPLOAD (AND)');
        this.availableActions = ['FIX_CHANGES', 'REUPLOAD'];
        this.data.actionLogic = 'AND';
        this.data.mismatchActions = ['FIX_CHANGES', 'REUPLOAD'];
      }

      this.modalMode = this.determineModalMode();

      // Setup for VERIFY_CHANGES mode (randomize options)
      if (this.modalMode === 'VERIFY_CHANGES') {
        this.setupVerifyOptions();
      }

      // Legacy: Check if it is only a name mismatch
      if (data.issues && data.issues.length === 1 && data.issues.includes('names_mismatch')) {
        this.isNameMismatchOnly = true;
      }
    }

    console.log('[Legal Name Fix] Modal opened with data:', {
      userId: data.userId,
      kycCheckId: data.kycCheckId,
      doneActionId: data.doneActionId,
      currentLegalName: data.currentLegalName,
      veriffName: data.veriffName,
      hasValidDocumentData,
      issues: data.issues,
      documentData: data.documentData,
      // New fields
      modalMode: this.modalMode,
      availableActions: this.availableActions,
      actionLogic: data.actionLogic,
      category: data.category,
      subCategory: data.subCategory,
    });
  }

  /**
   * Determine the modal mode based on available actions
   * Priority: VERIFY > FIX > ACCEPT > REUPLOAD > LEGACY
   */
  private determineModalMode(): ModalMode {
    // If no mismatch actions provided, use legacy mode
    if (this.availableActions.length === 0) {
      return 'LEGACY';
    }

    // Priority order
    if (this.availableActions.includes('VERIFY_CHANGES')) {
      return 'VERIFY_CHANGES';
    }
    if (this.availableActions.includes('FIX_CHANGES')) {
      return 'FIX_CHANGES';
    }
    if (this.availableActions.includes('ACCEPT_CHANGES')) {
      return 'ACCEPT_CHANGES';
    }
    if (this.availableActions.includes('REUPLOAD')) {
      return 'REUPLOAD';
    }

    // Fallback to LEGACY
    return 'LEGACY';
  }

  /**
   * Setup options A/B for VERIFY_CHANGES mode
   * Randomize order so user can't guess which is which
   */
  private setupVerifyOptions() {
    // Randomize which name goes to which option
    this.optionAIsLegal = Math.random() > 0.5;

    if (this.optionAIsLegal) {
      this.optionA = this.currentName || this.data.currentLegalName || '';
      this.optionB = this.veriffName;
    } else {
      this.optionA = this.veriffName;
      this.optionB = this.currentName || this.data.currentLegalName || '';
    }
  }

  // ===========================================================================
  // FIX ATTEMPTS TRACKING
  // ===========================================================================

  /**
   * Map current modalMode to FixAttemptType for logging
   */
  private get currentAttemptType(): FixAttemptType {
    switch (this.modalMode) {
      case 'ACCEPT_CHANGES': return 'ACCEPT';
      case 'FIX_CHANGES': return 'FIX';
      case 'VERIFY_CHANGES': return 'VERIFY';
      case 'REUPLOAD': return 'REUPLOAD';
      default: return 'FIX'; // LEGACY fallback
    }
  }

  /**
   * Build base DTO with common fields (context)
   */
  private baseDTO(overrides: Partial<LogFixAttemptDTO>): LogFixAttemptDTO {
    return {
      kyc_check_id: this.data.kycCheckId || this.data.doneActionId || '',
      attempt_type: this.currentAttemptType,
      success: false,
      reason: 'cancelled',
      previous_name: this.currentName || undefined,
      veriff_name: this.veriffName || undefined,
      category: this.data.category || undefined,
      action_logic: this.data.actionLogic || undefined,
      ...overrides,
    };
  }

  async ngOnInit() {
    await this.loadUserData();
  }

  async loadUserData() {
    this.loading = true;
    this.error = '';

    try {
      // Buscar usuário com PII
      const userWithPii = await this.userService.getWithPII(this.data.userId);

      if (userWithPii && userWithPii.pii) {
        this.currentName = userWithPii.pii.legal_name || '';
        this.newLegalName = this.currentName;

        // Re-setup verify options with updated name
        if (this.modalMode === 'VERIFY_CHANGES') {
          this.setupVerifyOptions();
        }

        // Formatar data de nascimento se existir
        if (userWithPii.pii.date_of_birth) {
          // Se for string no formato YYYY-MM-DD, parsear diretamente
          const dateValue: any = userWithPii.pii.date_of_birth;
          if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            const [year, month, day] = dateValue.split('-');
            this.currentDateOfBirth = `${day}/${month}/${year}`;
            this.newDateOfBirthDate = this.parseISODate(dateValue);
          } else {
            const date = new Date(dateValue);
            this.currentDateOfBirth = this.formatDate(date);
            this.newDateOfBirthDate = date;
          }
          this.newDateOfBirth = this.currentDateOfBirth;
        }

        // CPF
        this.currentCpf = userWithPii.pii.id_document_value || '';
        this.newCpf = this.currentCpf;

        // Inicializar cpfQuestion com valor atual se existir
        if (this.currentCpf) {
          this.cpfQuestion.answer = {
            value: this.currentCpf
          };
        }
      } else {
        this.error = 'Could not load user information';
      }
    } catch (err) {
      console.error('[Legal Name Fix] Error loading user data:', err);
      this.error = 'Error loading user information';
    } finally {
      this.loading = false;
    }
  }

  // ===========================================================================
  // ACCEPT_CHANGES: Accept micro change from document
  // ===========================================================================

  /**
   * User accepts the change from the document
   */
  async acceptChanges() {
    this.saving = true;
    this.error = '';

    // #1 ACCEPT attempt — logged BEFORE profile/revalidate
    this.kycService.logFixAttempt(this.baseDTO({
      attempt_type: 'ACCEPT',
      success: true,
      reason: 'accepted',
    }));

    try {
      const revalidationResult = await this.updateLegalNameOnly(this.veriffName);

      this.dialogRef.close({
        updated: true,
        revalidated: revalidationResult !== null,
        passed: revalidationResult?.allPassed ?? null,
        newLegalName: this.veriffName,
        kycCheckId: this.data.kycCheckId,
        action: 'accepted',
        message: this.translate.instant('identityVerified')
      });
    } catch (err: any) {
      console.error('[Legal Name Fix] Error accepting changes:', err);

      // #2 ACCEPT failure
      this.kycService.logFixAttempt(this.baseDTO({
        attempt_type: 'ACCEPT',
        success: false,
        reason: 'cancelled',
      }));

      if (err?.code === 'NAME_TOO_DIFFERENT_FROM_SERPRO') {
        this.error = this.translate.instant('NameTooDifferentFromVerifiedIdentity');
      } else {
        this.error = 'Erro ao atualizar nome. Tente novamente.';
      }
      this.saving = false;
    }
  }

  /**
   * User rejects the change (go to reupload)
   */
  rejectChanges() {
    this.mismatchStep = 'reupload_confirm';
  }

  // ===========================================================================
  // FIX_CHANGES: Type correct name (blind input)
  // ===========================================================================

  /**
   * User types name in blind input field
   */
  onFixedNameChange(value: string) {
    this.fixedName = value;
    this.nameVerified = false;
    this.verifyError = '';
  }

  /**
   * Verify/Save the typed name
   * 
   * - TRY_FIX: Always revalidate after saving. If fails, go to reupload
   * - AND logic (FIX + REUPLOAD): Skip verification, just save and go to reupload
   * - Other cases: Verify name matches document before saving
   */
  async verifyFixedName() {
    if (!this.fixedName.trim()) {
      this.verifyError = 'Digite o nome completo';
      return;
    }

    this.verifying = true;
    this.verifyError = '';

    console.log('[Legal Name Fix] Verifying fixed name:', {
      fixedName: this.fixedName,
      actionLogic: this.data.actionLogic,
      fromVerifyOptionC: this.fromVerifyOptionC,
      hasReuploadAction: this.data.mismatchActions?.includes('REUPLOAD')
    });

    try {
      // 🔧 AND REUPLOAD: No verification needed - user is providing correct name
      // and will upload new document anyway
      if (this.data.actionLogic === 'AND' && this.data.mismatchActions?.includes('REUPLOAD')) {
        console.log('[Legal Name Fix] AND flow: Skipping verification, saving and proceeding to REUPLOAD');
        this.nameVerified = true;
        await this.saveFixedNameForReupload();
        return;
      }

      // 🔧 TRY_FIX: Skip verification, save and revalidate (handled in saveFixedName)
      if (this.data.actionLogic === 'TRY_FIX') {
        console.log('[Legal Name Fix] TRY_FIX flow: Skipping verification, will revalidate after save');
        this.nameVerified = true;
        await this.saveFixedName();
        return;
      }

      // 🔧 CASE C (from VERIFY_CHANGES option_c): User is typing a completely new name
      // that may not match either currentName or veriffName. We need to:
      // 1. Save the name
      // 2. Revalidate
      // 3. If revalidate passes, close with success
      // 4. If revalidate fails, go to reupload
      if (this.fromVerifyOptionC) {
        console.log('[Legal Name Fix] Case C: Saving typed name and revalidating');
        this.nameVerified = true;
        await this.saveFixedName();
        return;
      }

      // Normal flow: Verify name matches document
      const normalizedFixed = this.normalizeName(this.fixedName);
      const normalizedVeriff = this.normalizeName(this.veriffName);

      if (normalizedFixed === normalizedVeriff) {
        this.nameVerified = true;
        // Save automatically when verified
        await this.saveFixedName();
      } else {
        // #3 FIX no_match
        this.kycService.logFixAttempt(this.baseDTO({
          attempt_type: 'FIX',
          success: false,
          reason: 'no_match',
          typed_name: this.fixedName,
        }));

        this.verifyError = 'O nome digitado não confere com o documento. Verifique e tente novamente.';
        this.nameVerified = false;
      }
    } catch (err) {
      this.verifyError = 'Erro ao verificar nome. Tente novamente.';
    } finally {
      this.verifying = false;
    }
  }

  /**
   * Save the fixed name after verification (normal flow)
   * Calls revalidate after saving
   * 
   * - TRY_FIX: Se revalidação falhar, vai para reupload
   * - Case C (fromVerifyOptionC): Se revalidação falhar, vai para reupload
   * - Normal: Fecha com sucesso independente de revalidação
   */
  async saveFixedName() {
    this.saving = true;
    this.error = '';

    // #4 FIX saved — logged BEFORE profile/revalidate
    this.kycService.logFixAttempt(this.baseDTO({
      attempt_type: 'FIX',
      success: true,
      reason: 'saved',
      typed_name: this.fixedName.trim(),
    }));

    try {
      const revalidationResult = await this.updateLegalNameOnly(this.fixedName.trim(), true); // with revalidate

      const allPassed = revalidationResult?.allPassed ?? false;

      console.log('[Legal Name Fix] FIX save + revalidate complete:', {
        allPassed,
        actionLogic: this.data.actionLogic,
        fromVerifyOptionC: this.fromVerifyOptionC,
        willGoToReupload: (this.data.actionLogic === 'TRY_FIX' || this.fromVerifyOptionC) && !allPassed
      });

      // 🔧 TRY_FIX: Se revalidação falhou, ir para reupload
      if (this.data.actionLogic === 'TRY_FIX' && !allPassed) {
        console.log('[Legal Name Fix] TRY_FIX: Revalidation failed, going to reupload');
        this.saving = false;
        this.mismatchStep = 'reupload_confirm';
        return;
      }

      // 🔧 CASE C: If revalidation failed, go to reupload instead of closing
      if (this.fromVerifyOptionC && !allPassed) {
        console.log('[Legal Name Fix] Case C: Revalidation failed, going to reupload');
        this.saving = false;
        this.mismatchStep = 'reupload_confirm';
        return;
      }

      // Close with success (either passed revalidation or not from case C/TRY_FIX)
      this.dialogRef.close({
        updated: true,
        revalidated: revalidationResult !== null,
        passed: allPassed,
        newLegalName: this.fixedName.trim(),
        kycCheckId: this.data.kycCheckId,
        action: 'fixed',
        message: allPassed 
          ? 'Nome corrigido e verificação concluída com sucesso!'
          : 'Nome corrigido com sucesso!'
      });
    } catch (err: any) {
      console.error('[Legal Name Fix] Error saving fixed name:', err);

      // #5 FIX saved failure
      this.kycService.logFixAttempt(this.baseDTO({
        attempt_type: 'FIX',
        success: false,
        reason: 'cancelled',
        typed_name: this.fixedName.trim(),
      }));

      if (err?.code === 'NAME_TOO_DIFFERENT_FROM_SERPRO') {
        this.error = this.translate.instant('NameTooDifferentFromVerifiedIdentity');
      } else {
        this.error = 'Erro ao salvar nome. Tente novamente.';
      }
      this.saving = false;
    }
  }

  /**
   * FIX_CHANGES: Go to reupload flow
   */
  goToReuploadFromFix() {
    this.mismatchStep = 'reupload_confirm';
  }

  // ===========================================================================
  // VERIFY_CHANGES: Choose which name is correct
  // ===========================================================================

  /**
   * User selects an option
   */
  selectVerifyOption(option: VerifyChoice) {
    this.verifyChoice = option;
    this.error = '';
  }

  /**
   * Confirm choice in VERIFY_CHANGES mode
   */
  async confirmVerifyChoice() {
    if (!this.verifyChoice) {
      this.error = 'Selecione uma opção';
      return;
    }

    this.saving = true;
    this.error = '';

    console.log('[Legal Name Fix] VERIFY choice confirmed:', {
      verifyChoice: this.verifyChoice,
      optionA: this.optionA,
      optionB: this.optionB,
      optionAIsLegal: this.optionAIsLegal,
      currentName: this.currentName,
      veriffName: this.veriffName
    });

    try {
      if (this.verifyChoice === 'option_c') {
        // #11 VERIFY chose_neither
        this.kycService.logFixAttempt(this.baseDTO({
          attempt_type: 'VERIFY',
          success: true,
          reason: 'chose_neither',
          chosen_option: 'option_c',
        }));

        // Option C: Neither is correct -> go to FIX_CHANGES flow
        // After FIX, will TRY to revalidate. If fails, goes to REUPLOAD
        // Use TRY_FIX logic: VERIFY -> option_c -> FIX -> Revalidate -> (pass: close | fail: REUPLOAD)
        this.data.actionLogic = 'TRY_FIX';
        this.data.mismatchActions = ['FIX_CHANGES', 'REUPLOAD'];
        this.modalMode = 'FIX_CHANGES';
        this.mismatchStep = 'fix_input';
        this.fromVerifyOptionC = true; // Mark that we came from VERIFY option C
        this.saving = false;
        return;
      }

      // Determine which name was chosen
      const choseOptionA = this.verifyChoice === 'option_a';
      const choseLegalName = (choseOptionA && this.optionAIsLegal) || (!choseOptionA && !this.optionAIsLegal);
      
      // Determine which actual name string was chosen
      const chosenName = choseOptionA ? this.optionA : this.optionB;

      console.log('[Legal Name Fix] VERIFY decision:', {
        choseOptionA,
        choseLegalName,
        chosenName,
        willUpdateTo: chosenName
      });

      // Log the choice
      this.kycService.logFixAttempt(this.baseDTO({
        attempt_type: 'VERIFY',
        success: true,
        reason: choseLegalName ? 'chose_legal' : 'chose_veriff',
        chosen_option: this.verifyChoice!,
      }));

      // Update legal name to the chosen value and revalidate
      console.log('[Legal Name Fix] VERIFY updating legal name to chosen value and revalidating');
      const revalidationResult = await this.updateLegalNameOnly(chosenName);
      console.log('[Legal Name Fix] VERIFY revalidation result:', revalidationResult);

      const allPassed = revalidationResult?.allPassed ?? false;

      // If revalidation failed, check if we should go to reupload
      // (This handles cases where the chosen name still doesn't fix the issue)
      if (!allPassed && this.needsReupload) {
        console.log('[Legal Name Fix] VERIFY revalidation failed, going to reupload');
        this.dialogRef.close({
          updated: true,
          resubmitDocuments: true,
          newLegalName: chosenName,
          kycCheckId: this.data.kycCheckId,
          action: 'reupload',
          message: 'Nome atualizado. Por favor, envie um novo documento.'
        });
      } else {
        // Close with success
        this.dialogRef.close({
          updated: true,
          revalidated: revalidationResult !== null,
          passed: allPassed,
          newLegalName: chosenName,
          kycCheckId: this.data.kycCheckId,
          action: 'accepted',
          message: allPassed 
            ? 'Nome atualizado e verificação concluída com sucesso!'
            : 'Nome atualizado com sucesso!'
        });
      }
    } catch (err: any) {
      console.error('[Legal Name Fix] Error confirming verify choice:', err);

      // #10 VERIFY failure
      this.kycService.logFixAttempt(this.baseDTO({
        attempt_type: 'VERIFY',
        success: false,
        reason: 'cancelled',
        chosen_option: this.verifyChoice ?? undefined,
      }));

      if (err?.code === 'NAME_TOO_DIFFERENT_FROM_SERPRO') {
        this.error = this.translate.instant('NameTooDifferentFromVerifiedIdentity');
      } else {
        this.error = 'Erro ao processar escolha. Tente novamente.';
      }
      this.saving = false;
    }
  }

  // ===========================================================================
  // REUPLOAD: Request new document
  // ===========================================================================

  /**
   * Confirm document reupload
   */
  confirmReupload() {
    // #12 REUPLOAD confirmed
    this.kycService.logFixAttempt(this.baseDTO({
      attempt_type: 'REUPLOAD',
      success: true,
      reason: 'reupload_confirmed',
    }));

    this.dialogRef.close({
      updated: false,
      resubmitDocuments: true,
      action: 'reupload',
      message: 'Por favor, envie um novo documento.'
    });
  }

  /**
   * Go back from reupload step
   */
  backFromReupload() {
    this.mismatchStep = 'main';
  }

  // ===========================================================================
  // Utilities
  // ===========================================================================

  /**
   * Normalize name for comparison
   */
  private normalizeName(name: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9 ]+/g, ' ')     // Remove punctuation
      .replace(/\s+/g, ' ')             // Normalize spaces
      .trim();
  }

  /**
   * Save fixed name for AND flow (FIX + REUPLOAD)
   * Does NOT call revalidate - user will upload new document
   */
  async saveFixedNameForReupload() {
    this.saving = true;
    this.error = '';

    // #6 FIX saved_for_reupload — logged BEFORE profile call
    this.kycService.logFixAttempt(this.baseDTO({
      attempt_type: 'FIX',
      success: true,
      reason: 'saved_for_reupload',
      typed_name: this.fixedName.trim(),
    }));

    try {
      await this.updateLegalNameOnly(this.fixedName.trim(), false); // NO revalidate

      console.log('[Legal Name Fix] AND flow: FIX complete, proceeding to REUPLOAD');
      this.saving = false;
      this.mismatchStep = 'reupload_confirm';
    } catch (err: any) {
      console.error('[Legal Name Fix] Error saving fixed name for reupload:', err);

      // #7 FIX saved_for_reupload failure
      this.kycService.logFixAttempt(this.baseDTO({
        attempt_type: 'FIX',
        success: false,
        reason: 'cancelled',
        typed_name: this.fixedName.trim(),
      }));

      if (err?.code === 'NAME_TOO_DIFFERENT_FROM_SERPRO') {
        this.error = this.translate.instant('NameTooDifferentFromVerifiedIdentity');
      } else {
        this.error = 'Erro ao salvar nome. Tente novamente.';
      }
      this.saving = false;
    }
  }

  /**
   * Update only the legal_name in the backend (for new modes)
   * @param newName - The new legal name
   * @param shouldRevalidate - Whether to call revalidate after saving (false for AND flows)
   * @returns Revalidation result if performed, null otherwise
   */
  private async updateLegalNameOnly(
    newName: string,
    shouldRevalidate: boolean = true
  ): Promise<{ allPassed: boolean; result: any } | null> {
    const token = (await this.userService.supabase.auth.getSession()).data
      .session?.access_token;

    try {
      await firstValueFrom(
        this.http.post(
          environment.lambdaUrl + '/profile',
          {
            legal_name: newName.trim(),
            ...(this.data.userId ? { user_id: this.data.userId } : {})
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      );
    } catch (httpError: any) {
      // [SECURITY] Backend SERPRO guard rejected the name change.
      // Signal caller with a specific error so UX can show a friendly message.
      const errorCode = httpError?.error?.details?.error_code;
      if (httpError?.status === 422 && errorCode === 'NAME_TOO_DIFFERENT_FROM_SERPRO') {
        const guardError = new Error('NAME_TOO_DIFFERENT_FROM_SERPRO');
        (guardError as any).code = 'NAME_TOO_DIFFERENT_FROM_SERPRO';
        throw guardError;
      }
      throw httpError;
    }

    console.log('[Legal Name Fix] Legal name updated to:', newName);

    // Revalidate KYC if we have done_action_id or kycCheckId AND shouldRevalidate is true
    // For AND flows (FIX + REUPLOAD), we skip revalidate because user will upload new doc
    const kycIdForRevalidation = this.data.doneActionId || this.data.kycCheckId;
    
    if (shouldRevalidate && kycIdForRevalidation) {
      console.log('[Legal Name Fix] Revalidating KYC check with ID:', {
        doneActionId: this.data.doneActionId,
        kycCheckId: this.data.kycCheckId,
        usingId: kycIdForRevalidation
      });
      try {
        const revalidationResult = await this.kycService.revalidateKycCheck(kycIdForRevalidation);
        console.log('[Legal Name Fix] Revalidation result:', revalidationResult);
        return {
          allPassed: revalidationResult?.validationSummary?.allPassed ?? false,
          result: revalidationResult
        };
      } catch (err) {
        console.warn('[Legal Name Fix] Revalidation failed:', err);
        return { allPassed: false, result: null };
      }
    } else if (!shouldRevalidate) {
      console.log('[Legal Name Fix] Skipping revalidate (AND flow - will reupload)');
    } else {
      console.warn('[Legal Name Fix] Cannot revalidate - no ID provided', {
        doneActionId: this.data.doneActionId,
        kycCheckId: this.data.kycCheckId
      });
    }
    
    return null;
  }

  // ===========================================================================
  // Getters for template
  // ===========================================================================

  get modalTitle(): string {
    if (this.modalMode === 'LEGACY') {
      return this.currentStep === 'correct-document'
        ? 'Please provide up-to-date documents'
        : 'Verify Your Information';
    }

    if (this.mismatchStep === 'reupload_confirm') {
      return 'Documento Necessário';
    }

    if (this.mismatchStep === 'fix_input') {
      return 'Corrigir Nome';
    }

    switch (this.modalMode) {
      case 'ACCEPT_CHANGES':
        return 'Verificar Nome';
      case 'FIX_CHANGES':
        return 'Corrigir Nome';
      case 'VERIFY_CHANGES':
        return 'Qual nome está correto?';
      case 'REUPLOAD':
        return 'Documento Necessário';
      default:
        return 'Verificar Informações';
    }
  }

  get canConfirmVerify(): boolean {
    return this.verifyChoice !== null && !this.saving;
  }

  get canVerifyFix(): boolean {
    return this.fixedName.trim().length > 0 && !this.verifying && !this.saving;
  }

  get showDiff(): boolean {
    // ACCEPT_CHANGES shows diff (before -> after)
    // VERIFY_CHANGES does NOT show diff (shows options without labels)
    return this.modalMode === 'ACCEPT_CHANGES';
  }

  get isNewMode(): boolean {
    return this.modalMode !== 'LEGACY';
  }

  get needsReupload(): boolean {
    return this.availableActions.includes('REUPLOAD');
  }

  /**
   * Verifica se o erro é B2b ou B3b (Veriff reading error)
   * B2b/B3b = Veriff está errada, nome legal está correto
   */
  get isVeriffReadingError(): boolean {
    const category = this.data.category;
    const subCategory = this.data.subCategory;
    return (category === 'B2' || category === 'B3') && subCategory === 'b';
  }

  formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatDateStringToDisplay(dateStr: string): string {
    // Converte YYYY-MM-DD para DD/MM/YYYY
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }

  parseISODate(dateStr: string): Date | null {
    // Parse YYYY-MM-DD para Date
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (isNaN(date.getTime())) return null;
    return date;
  }

  parseDate(dateStr: string): Date | null {
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;

    return date;
  }

  onNameChange(value: string) {
    this.newLegalName = value;
    this.nameError = '';
  }

  onDateOfBirthChange(value: any) {
    // O componente input-date retorna um Moment object ou Date
    this.dateOfBirthError = '';

    if (!value) {
      this.newDateOfBirthDate = null;
      this.newDateOfBirth = '';
      return;
    }

    // Se for Moment object, converter para Date
    let dateObj: Date;
    if (value._isAMomentObject) {
      dateObj = value.toDate();
    } else if (value instanceof Date) {
      dateObj = value;
    } else {
      this.dateOfBirthError = 'Invalid date format';
      return;
    }

    // Validar se a data é válida
    if (isNaN(dateObj.getTime())) {
      this.dateOfBirthError = 'Invalid date';
      return;
    }

    this.newDateOfBirthDate = dateObj;
    this.newDateOfBirth = this.formatDate(dateObj);
  }

  onCpfAnswerChange(answerData: QuestionAnswer) {
    this.newCpf = answerData.answer.value?.toString() || '';
    this.cpfError = '';

    // Verificar se o CPF foi validado e está leaked
    if (answerData.answer.addtional_information) {
      const info = answerData.answer.addtional_information as {
        leaked: boolean;
        checkedAt: Date;
      };
      this.cpfLeaked = info.leaked || false;
    } else {
      this.cpfLeaked = false;
    }

    // Validar se está preenchido corretamente
    if (!answerData.question.isValid) {
      this.cpfError = 'Please check CPF for leaks';
    }
  }

  formatCpfDisplay(cpf: string): string {
    if (!cpf) return '';
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
  }

  goToEditStep() {
    this.currentStep = 'edit';
  }

  goBackToConfirm() {
    this.currentStep = 'confirm';
  }

  // Action for "No, this is not my correct name" or "Submit a different document"
  goToCorrectDocumentStep() {
    this.currentStep = 'correct-document';
  }

  // Action for "Select new document" on the Correcting Document screen
  selectNewDocument() {
    this.dialogRef.close({
      updated: false,
      resubmitDocuments: true,
      message: 'Please resubmit your documents'
    });
  }

  // Action for "Yes, please amend what I typed in before"
  async amendData() {
    // Update local state with document data
    if (this.data.documentData) {
      if (this.data.documentData.legalName) {
        this.newLegalName = this.data.documentData.legalName;
      }

      if (this.data.documentData.dateOfBirth) {
        // Assuming dateOfBirth is YYYY-MM-DD
        const dateValue = this.data.documentData.dateOfBirth;
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
          const [year, month, day] = dateValue.split('-');
          this.newDateOfBirth = `${day}/${month}/${year}`;
          this.newDateOfBirthDate = this.parseISODate(dateValue);
        }
      }

      if (this.data.documentData.idNumber) {
        this.newCpf = this.data.documentData.idNumber;
        this.cpfLeaked = true;
      }
    }

    // Call save immediately
    await this.save();
  }

  confirmDataCorrect() {
    this.dialogRef.close({
      updated: false,
      resubmitDocuments: true,
      message: 'Please resubmit your documents'
    });
  }

  close() {
    // #13 CANCEL
    this.kycService.logFixAttempt(this.baseDTO({
      success: false,
      reason: 'cancelled',
    }));

    this.dialogRef.close({
      updated: false,
      action: 'cancelled'
    });
  }

  async save() {
    // Validações
    let hasError = false;

    if (!this.newLegalName || !this.newLegalName.trim()) {
      this.nameError = 'Please enter a valid name';
      hasError = true;
    }

    if (!this.newDateOfBirthDate || isNaN(this.newDateOfBirthDate.getTime())) {
      this.dateOfBirthError = 'Please select a valid date';
      hasError = true;
    }

    if (!this.newCpf || !this.newCpf.trim()) {
      this.cpfError = 'Please enter a valid CPF';
      hasError = true;
    }

    if (!this.cpfLeaked) {
      this.cpfError = 'CPF must be checked and found in leak database';
      hasError = true;
    }

    if (hasError) {
      return;
    }

    this.saving = true;
    this.error = '';

    try {
      const token = (await this.userService.supabase.auth.getSession()).data
        .session?.access_token;

      // Converter data para formato YYYY-MM-DD (sem hora)
      if (!this.newDateOfBirthDate) {
        this.dateOfBirthError = 'Invalid date';
        this.saving = false;
        return;
      }

      // Formatar data como YYYY-MM-DD
      const year = this.newDateOfBirthDate.getFullYear();
      const month = String(this.newDateOfBirthDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.newDateOfBirthDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      console.log('[Legal Name Fix] Sending data:', {
        legal_name: this.newLegalName.trim(),
        date_of_birth: dateString,
        id_document_value: this.newCpf.trim(),
        ...(this.data.userId ? { user_id: this.data.userId } : {})
      });

      await firstValueFrom(
        this.http.post(
          environment.lambdaUrl + '/profile',
          {
            legal_name: this.newLegalName.trim(),
            date_of_birth: dateString,
            id_document_value: this.newCpf.trim(),
            ...(this.data.userId ? { user_id: this.data.userId } : {})
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      );

      console.log('[Legal Name Fix] Data updated successfully');

      // Chamar revalidateKycCheck para verificar se agora passa nas validações internas
      console.log('[Legal Name Fix] Revalidating KYC check:', this.data.kycCheckId);
      console.log('[Legal Name Fix] Using done_action_id:', this.data.doneActionId);

      if (!this.data.doneActionId) {
        console.warn('[Legal Name Fix] No done_action_id provided, skipping revalidation');
        this.dialogRef.close({
          updated: true,
          revalidated: false,
          newLegalName: this.newLegalName.trim(),
          newDateOfBirth: dateString,
          newCpf: this.newCpf.trim(),
          kycCheckId: this.data.kycCheckId,
          message: 'Information updated successfully.'
        });
        return;
      }

      try {
        const revalidationResult = await this.kycService.revalidateKycCheck(this.data.doneActionId);
        console.log('[Legal Name Fix] Revalidation result:', revalidationResult);

        // Acessar passed e stage dentro de data
        const passed = revalidationResult.data.passed;
        const stage = revalidationResult.data.stage;
        const allPassed = revalidationResult.validationSummary.allPassed;

        console.log('[Legal Name Fix] Revalidation data:', { passed, stage, allPassed });

        // Verificar se a revalidação passou
        if (passed === true && stage === 'DONE') {
          console.log('[Legal Name Fix] ✅ Revalidation PASSED - KYC now approved!');

          // Retorna sucesso completo
          this.dialogRef.close({
            updated: true,
            revalidated: true,
            passed: true,
            newLegalName: this.newLegalName.trim(),
            newDateOfBirth: dateString,
            newCpf: this.newCpf.trim(),
            kycCheckId: this.data.kycCheckId,
            message: 'Information updated and verification completed successfully!'
          });
        } else if (stage === 'DONE' && passed === false) {
          console.log('[Legal Name Fix] ❌ Revalidation FAILED - Still has validation errors');

          // Ainda tem erros de validação, mas dados foram atualizados
          this.dialogRef.close({
            updated: true,
            revalidated: true,
            passed: false,
            newLegalName: this.newLegalName.trim(),
            newDateOfBirth: dateString,
            newCpf: this.newCpf.trim(),
            kycCheckId: this.data.kycCheckId,
            message: 'Information updated but verification still pending. Please upload valid documents.'
          });
        } else {
          console.log('[Legal Name Fix] ⏳ Revalidation in progress');

          // Está processando
          this.dialogRef.close({
            updated: true,
            revalidated: true,
            passed: null,
            newLegalName: this.newLegalName.trim(),
            newDateOfBirth: dateString,
            newCpf: this.newCpf.trim(),
            kycCheckId: this.data.kycCheckId,
            message: 'Information updated, verification in progress.'
          });
        }
      } catch (revalidationError) {
        console.error('[Legal Name Fix] Error during revalidation:', revalidationError);

        // Erro na revalidação, mas dados foram atualizados
        this.dialogRef.close({
          updated: true,
          revalidated: false,
          newLegalName: this.newLegalName.trim(),
          newDateOfBirth: dateString,
          newCpf: this.newCpf.trim(),
          kycCheckId: this.data.kycCheckId,
          message: 'Information updated. Please try uploading your documents again.'
        });
      }
    } catch (err) {
      console.error('[Legal Name Fix] Error updating data:', err);
      this.error = 'Error updating information. Please try again.';
      this.saving = false;
    }
  }

  get canSave(): boolean {
    const hasChanges =
      this.newLegalName.trim() !== this.currentName ||
      this.newDateOfBirth !== this.currentDateOfBirth ||
      this.newCpf.trim() !== this.currentCpf;

    const hasValidData =
      !!this.newLegalName &&
      this.newLegalName.trim() !== '' &&
      !!this.newDateOfBirthDate &&
      !isNaN(this.newDateOfBirthDate.getTime()) &&
      !!this.newCpf &&
      this.newCpf.trim() !== '' &&
      this.cpfLeaked;

    const hasNoErrors =
      !this.nameError &&
      !this.dateOfBirthError &&
      !this.cpfError;

    return (
      !this.saving &&
      !this.loading &&
      hasValidData &&
      hasChanges &&
      hasNoErrors
    );
  }

  get saveButtonText(): string {
    if (this.saving) {
      return 'Saving';
    }
    return 'UpdateInformation';
  }
}
